import { cdssApi } from "@/lib/api";
import { toast } from "sonner";

// All async step transitions for the clinical workflow. `s` is the state bundle
// from useWorkflowState; `run` wraps an async call with loading + error toast.
export function useWorkflowSteps(s, run, ageYears) {
  const submitSymptoms = async (e) => {
    e.preventDefault();
    if (ageYears == null) {
      toast.error("Please enter the patient's age (in years).");
      return;
    }
    if (ageYears < 5) {
      toast.error("This system supports patients aged 5 years and above. Please refer younger children to a pediatric specialist.");
      return;
    }
    const data = await run("Reviewing the history and preparing targeted follow-up questions…",
      () => cdssApi.followupQuestions({ model: s.model, patient: s.patient }));
    s.setQuestions(data);
    s.setAnswersReminded(false);
    s.setStep(1);
  };

  const submitAnswers = async () => {
    const qList = s.questions?.questions || [];
    const blanks = qList.filter((q) => !(s.answers[q.id] || "").trim());
    if (blanks.length > 0 && !s.answersReminded) {
      s.setAnswersReminded(true);
      toast.warning(`${blanks.length} follow-up question${blanks.length > 1 ? "s" : ""} left blank. Fill in for a sharper differential, or tap again to continue anyway.`);
      return;
    }
    const qa = qList.map((q) => ({ question: q.question, answer: s.answers[q.id] || "" }));
    const enriched = { ...s.patient, followup_answers: qa };
    s.setPatient(enriched);
    const data = await run("Forming the internal tentative differential…",
      () => cdssApi.tentative({ model: s.model, patient: enriched }));
    s.setTentative(data);
    s.setStep(2);
  };

  const toExamination = async () => {
    const data = await run("Identifying positive & negative signs for the doctor to elicit…",
      () => cdssApi.suggestedSigns({ model: s.model, patient: s.patient, tentative: s.tentative }));
    s.setSigns(data);
    s.setSignsReminded(false);
    s.setStep(3);
  };

  const submitFindings = async (e) => {
    e.preventDefault();
    const signList = s.signs?.signs || [];
    const unmarked = signList.filter((sg, i) => {
      const st = s.signResp[sg.id || i]?.status;
      return !st || st === "Not assessed";
    });
    if (signList.length > 0 && unmarked.length > 0 && !s.signsReminded) {
      s.setSignsReminded(true);
      toast.warning(`${unmarked.length} suggested sign${unmarked.length > 1 ? "s" : ""} not yet marked present/absent. Mark for a sharper differential, or tap again to continue.`);
      return;
    }
    const signsList = signList.map((sg, i) => ({
      sign: sg.sign,
      status: s.signResp[sg.id || i]?.status || "Not assessed",
      note: s.signResp[sg.id || i]?.note || "",
    }));
    const fullFindings = { ...s.findings, signs: signsList, scores: s.scores };
    s.setFindings(fullFindings);
    const data = await run("Integrating findings into a refined differential diagnosis…",
      () => cdssApi.differentials({ model: s.model, patient: s.patient, tentative: s.tentative, findings: fullFindings }));
    s.setDifferentials(data);
    s.setStep(4);
  };

  const orderInvestigations = async () => {
    const data = await run("Ordering investigations to discriminate the differentials…",
      () => cdssApi.investigations({ model: s.model, patient: s.patient, findings: s.findings, differentials: s.differentials }));
    s.setInvestigations(data);
    s.setReports((data.investigations || []).map((t) => ({ test: t.test, category: t.category, result: "" })));
    s.setStep(5);
  };

  const submitReports = async () => {
    const data = await run("Synthesising the final diagnosis & evidence-based treatment…",
      () => cdssApi.final({ model: s.model, patient: s.patient, findings: s.findings, differentials: s.differentials, reports: s.reports, imaging: s.imaging }));
    s.setFinal(data);
    s.setStep(6);
  };

  return { submitSymptoms, submitAnswers, toExamination, submitFindings, orderInvestigations, submitReports };
}
